import "./App.css";
import "./Components/Dashboard/Common/chatSurface.css";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import Login from "./Components/UserAuthentication/Login";
import Signup from "./Components/UserAuthentication/Signup";
import Logout from "./Components/UserAuthentication/Logout";
import ConfirmEmail from "./Components/UserAuthentication/ConfirmEmail";
import ConfirmationOverlay from "./Components/UserAuthentication/ConfirmationOverlay";
import ApproveUser from "./Components/UserAuthentication/ApproveUser";
import KeepAliveTabs from "./Components/Dashboard/Common/KeepAliveTabs";
import ProtectedRoute from "./Components/Dashboard/ProtectedRoute";
import AssistantProvider from "./Components/Dashboard/Common/AssistantHost";
import Sidebar from "./Components/Dashboard/Sidebar/Sidebar";
import UserProfile from "./Components/Dashboard/UserDetails/UserProfile";
import DocAssistant from "./Components/Dashboard/DocAssistant/DocAssistant";
import Home from "./Components/Dashboard/Home/Home";
import ApproveAccountId from "./Components/UserAuthentication/ApproveAccountId";
import ProductDashboardNoSubscription from "./Components/Dashboard/ProductDashboard/ProductDashboardNoSubscription";
import CoTraderNoSubscription from "./Components/Dashboard/CoTrader/CoTraderNoSubscription";
import ForgotPassword from "./Components/UserAuthentication/ForgotPassword";
import VerifyForgotPassword from "./Components/UserAuthentication/VerifyForgotPassword";
import Clarity from "@microsoft/clarity";
import FAQ from "./Components/Dashboard/FAQ/FAQ";
import MobileSidebar from "./Components/Dashboard/Sidebar/MbSidebar";
import HomeMobile from "./Components/Dashboard/Home/HomeMobile";
import UserProfileMobile from "./Components/Dashboard/UserDetails/UserProfileMobile";
import MobileHeader from "./Components/Dashboard/CoTrader/MobileHeader";
import TradingEconomicsCalendarMobile from "./Components/Dashboard/Calendar/TradingEconomicsCalendarMobile";
import TradingEconomicsCalendar from "./Components/Dashboard/Calendar/TradingEconomicsCalendar";
import PrivacyPolicy from "./Components/Legal/PrivacyPolicy";
import TermsOfUse from "./Components/Legal/TermsOfUse";
import DataDeletion from "./Components/Legal/DataDeletion";
import GetAppRedirectPage from "./Components/Dashboard/Common/GetAppRedirectPage";
import NoAccessPage from "./Components/Dashboard/Common/NoAccessPage";
import EmalphaPricingPageModern from "./Components/Pricing/EmalphaPricingPage";
import TradeAnalysis from "./Components/Dashboard/TradeAnalysis/TradeAnalysis";
import CoTraderDemoPage from "./Components/Dashboard/CoTrader/CoTraderDemoPage";
import { verifyLogin } from "./Components/utilities/authHelper";
import {
  readSidebarCollapsed,
  writeSidebarCollapsed,
} from "./Components/Dashboard/Sidebar/sidebarState";
import EmbedPage from "./Components/Embed/EmbedPage";
import EmbedLogin from "./Components/Embed/EmbedLogin";

const projectId = "st6sbi3zy1";
const isMobile = window.innerWidth < 768;

Clarity.init(projectId);

const createBaseTabs = () => [
  {
    name: "Home",
    subTabs: [],
    icon: "fa-house",
    roles: ["USER", "ADMIN"],
    path: "/home",
    component: isMobile ? <HomeMobile /> : <Home />,
  },
  {
    name: "Report Reader",
    subTabs: [],
    icon: "fa-book-open-reader",
    roles: ["USER", "ADMIN"],
    path: "/report-reader",
    component: <DocAssistant />,
  },
  {
    name: "Dashboard",
    subTabs: [],
    icon: "fa-table-columns",
    path: "/dashboard",
    component: <ProductDashboardNoSubscription />,
  },
  {
    name: "CoTrader",
    subTabs: [],
    icon: "fa-right-left",
    path: "/co-trader",
    component: <CoTraderNoSubscription />,
  },
  {
    name: "CoHedger",
    subTabs: [],
    icon: "fa-shield-halved",
    path: "/co-hedger",
    component: <CoTraderNoSubscription />,
  },
  {
    name: "Trade Analysis",
    subTabs: [],
    icon: "fa-magnifying-glass-chart",
    path: "/trade-analysis",
    component: <TradeAnalysis />,
  },
  {
    name: "Economic Calendar",
    subTabs: [],
    icon: "fa-calendar-days",
    roles: ["USER", "ADMIN"],
    path: "/economic-calendar",
    component: isMobile ? (
      <TradingEconomicsCalendarMobile />
    ) : (
      <TradingEconomicsCalendar />
    ),
    subscription_type: "GLOBALLINK",
  },
];

function App() {
  const [tabs, setTabs] = useState(() => createBaseTabs());
  const [tabsLoaded, setTabsLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => verifyLogin());
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readSidebarCollapsed);

  useEffect(() => {
    writeSidebarCollapsed(isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  const resetAppShell = () => {
    setTabs(createBaseTabs());
    setTabsLoaded(false);
  };

  const handleLoginSuccess = () => {
    resetAppShell();
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    resetAppShell();
    setIsAuthenticated(false);
  };

  const collapseSidebar = () => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  };

  return (
    <Router>
      <AssistantProvider
        enabled={isAuthenticated && tabsLoaded && !isMobile}
        tabs={tabs}
      >
      <div
        className="flex bg-custom "
        style={{ height: "100vh", width: "100vw" }}
      >
        <Routes>
          <Route
            path="/login"
            element={
              <Login
                isAuthenticated={isAuthenticated}
                onLoginSuccess={handleLoginSuccess}
              />
            }
          />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot_password" element={<ForgotPassword />} />
          <Route
            path="/logout"
            element={<Logout onLogout={handleLogout} />}
          />
          <Route path="/reset_password" element={<VerifyForgotPassword />} />
          <Route
            path="/confirm-email/:confirmationCode"
            element={<ConfirmEmail />}
          />
          <Route
            path="/approve_user/:approvalToken"
            element={<ApproveAccountId />}
          />

          <Route
            path="/approve-email/:approvalCode"
            element={<ApproveUser />}
          />
          <Route path="/pricing" element={<EmalphaPricingPageModern />} />
          <Route path="/confirm-email" element={<ConfirmationOverlay />} />
          <Route
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                tabs={tabs}
                setTabs={setTabs}
                setTabsLoaded={setTabsLoaded}
                tabsLoaded={tabsLoaded}
              >
                <div className=" d-flex  bg-custom" style={{ height: "100vh" }}>
                  <div>
                    {isMobile ? (
                      <MobileSidebar tabs={tabs} tabsLoaded={tabsLoaded} />
                    ) : (
                      <Sidebar
                        tabs={tabs}
                        tabsLoaded={tabsLoaded}
                        isCollapsed={isSidebarCollapsed}
                        setIsCollapsed={setIsSidebarCollapsed}
                      />
                    )}
                  </div>
                  <div
                    className="flex-grow-1"
                    onClick={collapseSidebar}
                    style={{
                      overflow: "auto",
                      height: isMobile ? "calc(100vh - 60px)" : "100%",
                    }}
                  >
                    <KeepAliveTabs tabs={tabs} />
                  </div>
                </div>
              </ProtectedRoute>
            }
          >
            {tabs.map((tab) => (
              <Route key={tab.name} path={tab.path} element={null} />
            ))}
          </Route>
          <Route
            key="profile"
            path="/profile"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                tabs={tabs}
                setTabs={setTabs}
                setTabsLoaded={setTabsLoaded}
                tabsLoaded={tabsLoaded}
              >
                <div className="d-flex bg-custom" style={{ height: "100vh" }}>
                  {/* Sidebar */}
                  <div style={{ flexShrink: 0 }}>
                    {isMobile ? (
                      <MobileSidebar tabs={tabs} tabsLoaded={tabsLoaded} />
                    ) : (
                      <Sidebar
                        tabs={tabs}
                        tabsLoaded={tabsLoaded}
                        isCollapsed={isSidebarCollapsed}
                        setIsCollapsed={setIsSidebarCollapsed}
                      />
                    )}
                  </div>

                  {/* Main area */}
                  {isMobile ? (
                    <div
                      className="d-flex flex-column"
                      style={{
                        flexGrow: 1,
                        minWidth: 0, // IMPORTANT in flex row
                        height: "100%",
                      }}
                    >
                      {/* Sticky mobile header */}
                      <div
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 20,
                          background: "#fff",
                          borderBottom: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        <MobileHeader />
                      </div>

                      {/* Scrollable content */}
                      <div
                        className="flex-grow-1"
                        style={{
                          minHeight: 0, // IMPORTANT in flex column for scrolling to work
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                          paddingBottom: "60px",
                        }}
                      >
                        <UserProfileMobile />
                      </div>
                    </div>
                  ) : (
	                    <div
	                      className="flex-grow-1"
	                      onClick={collapseSidebar}
	                      style={{
	                        minWidth: 0,
                        overflow: "auto",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <UserProfile />
                    </div>
                  )}
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            key="FAQ"
            path="/FAQ"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                tabs={tabs}
                setTabs={setTabs}
                setTabsLoaded={setTabsLoaded}
                tabsLoaded={tabsLoaded}
              >
                <div className=" d-flex  bg-custom" style={{ height: "100vh",width: "100vw", }}>
                  <div>
                    {isMobile ? (
                      <MobileSidebar tabs={tabs} tabsLoaded={tabsLoaded} />
                    ) : (
                      <Sidebar
                        tabs={tabs}
                        tabsLoaded={tabsLoaded}
                        isCollapsed={isSidebarCollapsed}
                        setIsCollapsed={setIsSidebarCollapsed}
                      />
                    )}
                  </div>

	                  <div
	                    style={{ height: "100%", width: "100%" }}
	                    onClick={collapseSidebar}
	                  >
                    {isMobile ? (
                      <div
                        className="d-flex flex-column"
                        style={{ height: "100dvh" }} // mobile-safe full height
                      >
                        {/* Sticky Header */}
                        <div
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 20,
                            background: "#fff",
                          }}
                        >
                          <MobileHeader />
                        </div>

                        {/* Scrollable FAQ */}
                        <div
                          className="flex-grow-1"
                          style={{
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                          }}
                        >
                          <FAQ />
                        </div>
                      </div>
                    ) : (
                      // Desktop / non-mobile layout
                      <div
                        className="flex-grow-1"
                        style={{ overflow: "auto", height: "100%" }}
                      >
                        <FAQ />
                      </div>
                    )}
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/trade_analysis"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                tabs={tabs}
                setTabs={setTabs}
                setTabsLoaded={setTabsLoaded}
                tabsLoaded={tabsLoaded}
              >
                <div className=" d-flex  bg-custom" style={{ height: "100vh" }}>
                  <div>
                    {isMobile ? (
                      <MobileSidebar tabs={tabs} tabsLoaded={tabsLoaded} />
                    ) : (
                      <Sidebar
                        tabs={tabs}
                        tabsLoaded={tabsLoaded}
                        isCollapsed={isSidebarCollapsed}
                        setIsCollapsed={setIsSidebarCollapsed}
                      />
                    )}
                  </div>
	                  <div
	                    className="flex-grow-1"
	                    onClick={collapseSidebar}
	                    style={{
	                      overflow: "auto",
                      height: isMobile ? "calc(100vh - 60px)" : "100%",
                    }}
                  >
                    <TradeAnalysis />
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/no-access"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                tabs={tabs}
                setTabs={setTabs}
                setTabsLoaded={setTabsLoaded}
                tabsLoaded={tabsLoaded}
              >
                <div className=" d-flex  bg-custom" style={{ height: "100vh" }}>
                  <div>
                    {isMobile ? (
                      <MobileSidebar tabs={tabs} tabsLoaded={tabsLoaded} />
                    ) : (
                      <Sidebar
                        tabs={tabs}
                        tabsLoaded={tabsLoaded}
                        isCollapsed={isSidebarCollapsed}
                        setIsCollapsed={setIsSidebarCollapsed}
                      />
                    )}
                  </div>
	                  <div
	                    className="flex-grow-1"
	                    onClick={collapseSidebar}
	                    style={{
	                      overflow: "auto",
                      height: isMobile ? "calc(100vh - 60px)" : "100%",
                    }}
                  >
                    <NoAccessPage />
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/co-trader-demo"
            element={
              <div
                className="w-100"
                style={{
                  height: isMobile ? "100dvh" : "100vh",
                  width: "100vw",
                  overflow: "hidden",
                }}
              >
                <CoTraderDemoPage />
              </div>
            }
          />
          {/* Legal Pages */}
          {/* Partner embed: launch URL landing + standalone screens */}
          <Route path="/embed-login/:loginToken" element={<EmbedLogin />} />
          <Route path="/embed/:screenKey" element={<EmbedPage />} />
          <Route path="/privacy_policy" element={<PrivacyPolicy />} />
          <Route path="/terms_of_use" element={<TermsOfUse />} />
          <Route path="/data_deletion" element={<DataDeletion />} />
          <Route path="/get_app" element={<GetAppRedirectPage />} />
          <Route
            path="/*"
            element={
              <Login
                isAuthenticated={isAuthenticated}
                onLoginSuccess={handleLoginSuccess}
              />
            }
          />
        </Routes>
      </div>
      </AssistantProvider>
    </Router>
  );
}

export default App;
